import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgencyAnnouncement, AgencyAnnouncementAcknowledgement } from "../../types/agencyAnnouncement";
import { archiveAgencyAnnouncement, escapeSingleQuotes, getAcknowledgementsForAgencyAnnouncement, getAgencyAnnouncementByID, publishAgencyAnnouncement, updateAgencyAnnouncement } from "../../apicalls/announcementService";
import { useAppContext } from "../../Context";
import moment from "moment";
import ReadOnlyText from "../../components/forms/readOnlyRichTextField";
import { ButtonErrorSecondary, ButtonPrimary, ButtonSecondary, ButtonWarning, ButtonWarningSecondary } from "../../components/buttons/Buttons";
import { Select } from "../../components/forms/formFields";
import RichTextField from "../../components/forms/richTextField";
import { CircularProgress } from "@mui/material";
import ConfirmDialog from "../../components/dialogs/confirmDialog";
import SuccessDialog from "../../components/dialogs/successDialog";
import { GenericBreadCrumbs } from "../Scheduling/SchedulingUtils";
import CalendarSelector from "../../components/calendars/CalendarSelector";
import { convertUTCDateToLocalDate } from "../../utilities/dateUtils";
import { LoadingSpinner } from "../../components/portalComponents/loadingSpinner";

const sampleAcknowledgments: AgencyAnnouncementAcknowledgement[] = [
    {
        agencyAnnouncementAcknowledgementID: "123",
        agencyAnnouncementID: "123",
        employeeID: "123",
        employeeName: "John Doe",
        acknowledgementDate: moment().add(-5, 'days').toDate()
    },
    {
        agencyAnnouncementAcknowledgementID: "456",
        agencyAnnouncementID: "123",
        employeeID: "456",
        employeeName: "Jane Doe",
        acknowledgementDate: moment().add(-3, 'days').toDate()
    },
    {
        agencyAnnouncementAcknowledgementID: "789",
        agencyAnnouncementID: "123",
        employeeID: "789",
        employeeName: "Mark Spencer",
        acknowledgementDate: moment().add(-2, 'days').toDate()
    }
]

const ManageAgencyAnnouncementPage = props => {
    const { announcementID } = useParams();

    const [loadingAnnouncement, setLoadingAnnouncement] = useState(true);

    const navContext = useAppContext();

    useLayoutEffect(() => {
        navContext.setFullScreen(false);
    }, []);

    let navigate = useNavigate();

    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const [showArchiveConfirmDialog, setShowArchiveConfirmDialog] = useState(false);
    const [showArchiveSuccessDialog, setShowArchiveSuccessDialog] = useState(false);

    const [showPublishConfirmDialog, setShowPublishConfirmDialog] = useState(false);
    const [showPublishSuccessDialog, setShowPublishSuccessDialog] = useState(false);

    const [announcement, setAnnouncement] = useState<AgencyAnnouncement | null>(null);

    const [acknowledgements, setAcknowledgements] = useState<AgencyAnnouncementAcknowledgement[]>(null);

    const [editMode, setEditMode] = useState<boolean>(false);

    const announcementTypeRef = useRef();

    const [savingAnnouncement, setSavingAnnouncement] = useState(false);
    
    const [formValues, setFormValues] = useState({
        announcementTitle: "",
        announcementDescription: "",
        announcementType: "",
        startDate: moment().format("M-D-YYYY"),
        dateOfExpiry: moment().add(14, 'days').format("M-D-YYYY")
    });

    const [formErrors, setFormErrors] = useState({
        announcementTitleError: false,
        announcementTitleErrorMessage: "",
        announcementDescriptionError: false,
        announcementDescriptionErrorMessage: "",
        announcementTypeError: false,
        announcementTypeErrorMessage: "",
        startDateError: false,
        startDateErrorMessage: "",
        dateOfExpiryError: false,
        dateOfExpiryErrorMessage: ""
    });

    useLayoutEffect(() => {

        const fetchAnnouncement = async () => {
            const result = await getAgencyAnnouncementByID(announcementID, navContext.handleAPICallError);

            setAnnouncement(result);

            setFormValues({
                announcementTitle: result.announcementTitle,
                announcementDescription: result.announcementDescription,
                announcementType: result.announcementType,
                // startDate: moment().format("M-D-YYYY"),
                // dateOfExpiry: moment().add(14, 'days').format("M-D-YYYY")
                startDate: result.startDate ? moment(result.startDate).format("M-D-YYYY") : moment().format("M-D-YYYY"),
                dateOfExpiry: result.dateOfExpiry ? moment(result.dateOfExpiry).format("M-D-YYYY") : moment().add(14, 'days').format("M-D-YYYY")
            });

            const acknowledgements = await getAcknowledgementsForAgencyAnnouncement(announcementID, navContext.handleAPICallError);

            console.log(acknowledgements);

            setAcknowledgements(acknowledgements);

            setLoadingAnnouncement(false);
        }

        if (announcementID) {
            // fetch announcement by ID
            fetchAnnouncement();
        }
    
    }, [announcementID])

    const validateStartDate = (value: string) => {
        if(value === '') {
            setFormErrors({...formErrors, startDateError: true, startDateErrorMessage: 'Start Date is required'});
        } else if (moment(value, "M-D-YYYY").isBefore(moment().startOf('day'))) {
            setFormErrors({...formErrors, startDateError: true, startDateErrorMessage: 'Start Date cannot be in the past'});
        } else {
            setFormErrors({...formErrors, startDateError: false, startDateErrorMessage: ''});

            setFormValues({...formValues, dateOfExpiry: moment(value, "M-D-YYYY").add(14, 'days').format('M-D-YYYY')});
        }
    }

    const validateDateOfExpiry = (value: string) => {
        if(value === '') {
            setFormErrors({...formErrors, dateOfExpiryError: true, dateOfExpiryErrorMessage: 'Date of Expiry is required'});
        } else if (moment(value, "M-D-YYYY").isSameOrBefore(moment(formValues.startDate, "M-D-YYYY"))) {
            setFormErrors({...formErrors, dateOfExpiryError: true, dateOfExpiryErrorMessage: 'Date of Expiry cannot be before Start Date'});
        } else {
            setFormErrors({...formErrors, dateOfExpiryError: false, dateOfExpiryErrorMessage: ''});
        }
    }

    const handleRichTextFieldChange = (value: string) => {
        console.log(value);

        setFormValues({...formValues, announcementDescription: value});

        validateAnnouncementDescription(value);
    }

    const validateAnnouncementTitle = (value: string) => {
        if(value === '') {
            setFormErrors({...formErrors, announcementTitleError: true, announcementTitleErrorMessage: 'Announcement title is required'});
        } else {
            setFormErrors({...formErrors, announcementTitleError: false, announcementTitleErrorMessage: ''});
        }
    }

    const validateAnnouncementDescription = (value: string) => {
        if(value === '') {
            setFormErrors({...formErrors, announcementDescriptionError: true, announcementDescriptionErrorMessage: 'Announcement description is required'});
        } else {
            setFormErrors({...formErrors, announcementDescriptionError: false, announcementDescriptionErrorMessage: ''});
        }
    }

    const validateAnnouncementType = (value: string) => {
        if(value === '') {
            setFormErrors({...formErrors, announcementTypeError: true, announcementTypeErrorMessage: 'Announcement type is required'});
        } else {
            setFormErrors({...formErrors, announcementTypeError: false, announcementTypeErrorMessage: ''});
        }
    }

    const confirmDiscardAnnouncementChanges = () => {
        setShowCancelDialog(true);
    }

    const discardAnnouncementChanges = () => {
        setShowCancelDialog(false);
        setEditMode(false);
        setFormValues({
            announcementTitle: announcement?.announcementTitle,
            announcementDescription: announcement?.announcementDescription,
            announcementType: announcement?.announcementType,
            startDate: announcement?.startDate ? moment(announcement.startDate).format("M-D-YYYY") : "",
            dateOfExpiry: announcement?.dateOfExpiry ? moment(announcement.dateOfExpiry).format("M-D-YYYY") : ""
        });
        setFormErrors({
            announcementTitleError: false,
            announcementTitleErrorMessage: "",
            announcementDescriptionError: false,
            announcementDescriptionErrorMessage: "",
            announcementTypeError: false,
            announcementTypeErrorMessage: "",
            startDateError: false,
            startDateErrorMessage: "",
            dateOfExpiryError: false,
            dateOfExpiryErrorMessage: ""
        });
    }

    const dismissCancelDialog = () => {
        setShowCancelDialog(false);
    }

    const dismissSuccessDialog = () => {
        setShowSuccessDialog(false);
        setEditMode(false);
    };

    const validateForm = () => {
        let valid = true;

        var currentErrors = {...formErrors};

        if(formValues.announcementTitle === '') {
            currentErrors.announcementTitleError = true;
            currentErrors.announcementTitleErrorMessage = 'Announcement title is required';
            valid = false;
        } else {
            currentErrors.announcementTitleError = false;
            currentErrors.announcementTitleErrorMessage = '';
        }

        if(formValues.announcementDescription === '') {
            currentErrors.announcementDescriptionError = true;
            currentErrors.announcementDescriptionErrorMessage = 'Announcement description is required';
            valid = false;
        } else {
            currentErrors.announcementDescriptionError = false;
            currentErrors.announcementDescriptionErrorMessage = '';
        }

        if(formValues.announcementType === '') {
            currentErrors.announcementTypeError = true;
            currentErrors.announcementTypeErrorMessage = 'Announcement type is required';
            valid = false;
        } else {
            currentErrors.announcementTypeError = false;
            currentErrors.announcementTypeErrorMessage = '';
        }

        if(formValues.startDate === '') {
            currentErrors.startDateError = true;
            currentErrors.startDateErrorMessage = 'Start Date is required';
            valid = false;
        } else if (moment(formValues.startDate, "M-D-YYYY").isBefore(moment().startOf('day'))) {
            currentErrors.startDateError = true;
            currentErrors.startDateErrorMessage = 'Start Date cannot be in the past';
            valid = false;
        } else {
            currentErrors.startDateError = false;
            currentErrors.startDateErrorMessage = '';
        }

        if(formValues.dateOfExpiry === '') {
            currentErrors.dateOfExpiryError = true;
            currentErrors.dateOfExpiryErrorMessage = 'Date of Expiry is required';
            valid = false;
        } else if (moment(formValues.dateOfExpiry, "M-D-YYYY").isSameOrBefore(moment(formValues.startDate, "M-D-YYYY"))) {
            currentErrors.dateOfExpiryError = true;
            currentErrors.dateOfExpiryErrorMessage = 'Date of Expiry cannot be before Start Date';
            valid = false;
        } else {
            currentErrors.dateOfExpiryError = false;
            currentErrors.dateOfExpiryErrorMessage = '';
        }

        setFormErrors(currentErrors);

        return valid;
    }

    const saveAgencyAnnouncement = async () => {
        console.log("Save Agency Announcement");

        const valid = validateForm();

        if(valid) {
            setSavingAnnouncement(true);

            var newAnnouncement: AgencyAnnouncement = {
                agencyAnnouncementID: announcementID,
                announcementTitle: escapeSingleQuotes(formValues.announcementTitle),
                announcementDescription: escapeSingleQuotes(formValues.announcementDescription),
                announcementType: formValues.announcementType,
                dateCreated: announcement?.dateCreated,
                createdBy: announcement?.createdBy,
                status: announcement?.status,
                startDate: moment(formValues.startDate, "M-D-YYYY").toDate(),
                dateOfExpiry: moment(formValues.dateOfExpiry, "M-D-YYYY").toDate(),
                publishedDate: undefined,
                publishedBy: "",
                archiveDate: undefined,
                archivedBy: ""
            }

            console.log(newAnnouncement);

            const result = await updateAgencyAnnouncement(newAnnouncement, navContext.handleAPICallError);

            setAnnouncement(newAnnouncement);

            setSavingAnnouncement(false);
            setShowSuccessDialog(true);
        }
    }

    const dismissArchiveConfirmDialog = () => {
        setShowArchiveConfirmDialog(false);
    }

    const confirmArchiveAnnouncement = () => {
        setShowArchiveConfirmDialog(true);
    }

    const archiveAnnouncement = async () => {
        console.log("Archive Announcement");

        setShowArchiveConfirmDialog(false);

        setSavingAnnouncement(true);

        const result = await archiveAgencyAnnouncement(announcementID, navContext.handleAPICallError);

        setSavingAnnouncement(false);

        setShowArchiveSuccessDialog(true);
    }

    const dismissArchiveSuccessDialog = () => {
        setShowArchiveSuccessDialog(false);
        setSavingAnnouncement(false);
        navigate(-1);
    }

    const dismissPublishConfirmDialog = () => {
        setShowPublishConfirmDialog(false);
    }

    const confirmPublishAnnouncement = () => {
        setShowPublishConfirmDialog(true);
    }

    const publishAnnouncement = async () => {
        console.log("Publish Announcement");

        setShowPublishConfirmDialog(false);

        setSavingAnnouncement(true);

        const result = await publishAgencyAnnouncement(announcementID, navContext.handleAPICallError);

        setAnnouncement({...announcement, status: "Active", publishedDate: new Date()});

        setSavingAnnouncement(false);

        setShowPublishSuccessDialog(true);
    }

    const dismissPublishSuccessDialog = () => {
        setShowPublishSuccessDialog(false);
        setSavingAnnouncement(false);
        //navigate(-1);
    }
    
    return (<>
        <SuccessDialog title="Announcement Updated" message="The agency announcement has been updated successfully." onClose={dismissSuccessDialog} closeButtonText="close" open={showSuccessDialog}/>
        <ConfirmDialog title="Discard Announcement Changes" message="Are you sure you want to discard changes to this announcement?" onConfirm={discardAnnouncementChanges} confirmButtonText={"Yes"} cancelButtonText="No" onCancel={dismissCancelDialog} showDialog={showCancelDialog}/>
        <SuccessDialog title="Announcement Archived" message="The agency announcement has been archived successfully." onClose={dismissArchiveSuccessDialog} closeButtonText="Back to announcements" open={showArchiveSuccessDialog}/>
        <ConfirmDialog title="Archive Announcement" message="Are you sure you want to archive this announcement? This is not reversible" onConfirm={archiveAnnouncement} confirmButtonText={"Yes"} cancelButtonText="No" onCancel={dismissArchiveConfirmDialog} showDialog={showArchiveConfirmDialog}/>
        <SuccessDialog title="Announcement Published" message="The agency announcement has been publish successfully." onClose={dismissPublishSuccessDialog} closeButtonText="Close" open={showPublishSuccessDialog}/>
        <ConfirmDialog title="Publish Announcement" message="Are you sure you want to publish this announcement?" onConfirm={publishAnnouncement} confirmButtonText={"Yes"} cancelButtonText="No" onCancel={dismissPublishConfirmDialog} showDialog={showPublishConfirmDialog}/>
        <div className="tw-px-xl tw-pt-xl">
                <div className="tw-flex tw-flex tw-flex-col tw-space-y-20px tw-text-dark">
                    <div className="tw-space-y-20px tw-pb-2 tw-border-b tw-border-gray-800">
					<GenericBreadCrumbs 
                    mainHeading={"Agency Announcements"}
                    subHeading={editMode ? "Edit" : "View"}
                    backURL={"/announcement/agency"}
					/>
			        </div>
                </div>

                {loadingAnnouncement ? <div className="tw-flex tw-flex-col tw-y-space-5px tw-justify-center tw-m-md">
                    <LoadingSpinner /> 
					<span className="tw-text-primary tw-text-5 tw-text-center">Loading Announcement</span>
                </div> : <>   

            {editMode ? <>
                <div className="tw-flex tw-flex-col tw-space-y-15px tw-leading-tight tw-mt-15px"> 
                    <div className="tw-flex tw-flex-row tw-w-full">
                        <div className="tw-flex tw-flex-col tw-space-y-5 tw-w-1/2">
                            <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Created By
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {announcement?.createdBy}
                                </span>
                            </li>

                            <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Date Created
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {moment(announcement?.dateCreated).format("MM/DD/YYYY h:mm A")}
                                </span>
                            </li>

                            <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Status
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {announcement?.status}
                                </span>
                            </li>

                            {announcement?.status === "Active" && <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Published Date
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {moment(announcement?.publishedDate).format("MM/DD/YYYY h:mm A")}
                                </span>
                            </li>}

                            {announcement?.status === "Active" && <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Published By
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {announcement?.publishedBy}
                                </span>
                            </li>}

                            {announcement?.status === "Archived" && <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Archived Date
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {moment(announcement?.archiveDate).format("MM/DD/YYYY h:mm A")}
                                </span>
                            </li>}

                            {announcement?.status === "Archived" && <li className="tw-flex tw-flex-col tw-space-y-5px">
                                <span className="tw-w-150px tw-flex">
                                    <span className="tw-opacity-70">
                                        Archived By
                                    </span>
                                </span>
                                <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                                    {announcement?.archivedBy}
                                </span>
                            </li>}
                        </div>
                        <div className="tw-flex tw-flex-col tw-space-y-1 tw-w-1/2">
                            <div className="tw-flex tw-flex-col tw-space-y-1">
                                <label className="tw-text-gray-700">Announcement Title</label>
                                <input
                                    type="text"
                                    min={0}
                                    value={formValues.announcementTitle}
                                    onChange={e => {
                                        setFormValues({...formValues, announcementTitle: e.target.value});
                                        validateAnnouncementTitle(e.target.value);
                                    }}
                                    className={`tw-px-3 tw-py-2 tw-border tw-bg-white ${formErrors.announcementTitleError ? "tw-border-red-500": "tw-border-primary"} tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring focus:tw-border-blue-300`}
                                    />
                                {formErrors.announcementTitleError && <p className="tw-text-red-500">{formErrors.announcementTitleErrorMessage}</p>}
                            </div>

                            <div className="tw-flex tw-flex-col tw-space-y-1 tw-w-1/2">
                                <label className="tw-text-gray-700">Announcement Type</label>
                                <Select id="announcement-type" className={formErrors.announcementTypeError ? '_form-error': ''} 
                                    onChange={ e => {
                                        console.log(e.target.value);
                                        setFormValues({...formValues, announcementType: e.target.value});
                                        validateAnnouncementType(e.target.value);
                                    }} ref={ announcementTypeRef } value={ formValues.announcementType }>
                                    <option value="">Select</option>
                                    <option value="Policy">Policy</option>
                                    <option value="Procedure">Procedure</option>
                                    <option value="Client Onboarding">Client Onboarding</option>
                                    <option value="Client Offboarding">Client Offboarding</option>
                                    <option value="Software Update">Software Update</option>
                                    <option value="Other">Other</option>
                                </Select>
                                {formErrors.announcementTypeError && <p className="tw-text-red-500">{formErrors.announcementTypeErrorMessage}</p>}
                            </div>

                            <div className="tw-flex tw-flex-col tw-space-y-1 tw-w-1/4 sm:tw-w-1/2">
                                <CalendarSelector id="startDate" className={formErrors.startDateError ? '_form-error' : ''} 
                                    name="Start Date:" value={ formValues.startDate || null } 
                                    onChange={ e => {
                                        console.log(e.target.value);
                                        setFormValues({...formValues, startDate: e.target.value});
                                        validateStartDate(e.target.value);
                                    }}/>
                                    {formErrors.startDateError && <p className="tw-text-red-500">{formErrors.startDateErrorMessage}</p>}
                            </div>

                            <div className="tw-flex tw-flex-col tw-space-y-1 tw-w-1/4 sm:tw-w-1/2">
                                <CalendarSelector id="dateOfExpiry" className={formErrors.dateOfExpiryError ? '_form-error' : ''} 
                                    name="Date Of Expiry:" value={ formValues.dateOfExpiry || null } 
                                    onChange={ e => {
                                        console.log(e.target.value);
                                        setFormValues({...formValues, dateOfExpiry: e.target.value});
                                        validateDateOfExpiry(e.target.value);
                                    }}/>
                                    {formErrors.dateOfExpiryError && <p className="tw-text-red-500">{formErrors.dateOfExpiryErrorMessage}</p>}
                            </div>
                        </div>    
                    </div>  
                    

                    

                    



                    <RichTextField label="Announcement Description" value={formValues.announcementDescription} onChange={handleRichTextFieldChange} showValidation={formErrors.announcementDescriptionError} validationMessage={formErrors.announcementDescriptionErrorMessage}/>

                    <div className="tw-flex tw-justify-end tw-mx-4">
                        {savingAnnouncement ? <>
                                <CircularProgress style={{ color: "#12B4B9" }} ></CircularProgress>
                            </>:<>
                                        <ButtonErrorSecondary className="tw-mt-md tw-ml-4" onClick={confirmArchiveAnnouncement}>
                                            Archive
                                        </ButtonErrorSecondary>
                                        <ButtonWarningSecondary className="tw-mt-md tw-mx-4" onClick={confirmDiscardAnnouncementChanges}>
                                            Discard
                                        </ButtonWarningSecondary>
                                        <ButtonPrimary className="tw-mt-md" type="submit" onClick={saveAgencyAnnouncement}>
                                            Save
                                        </ButtonPrimary>
                        </>}

                    </div>
            </div>
            </> : <>
                <ul className="tw-text-5 tw-flex tw-flex-col tw-space-y-15px tw-leading-tight tw-pb-5 tw-mt-15px">
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Title
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {announcement?.announcementTitle}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Created By
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {announcement?.createdBy}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Date Created
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {moment(announcement?.dateCreated).format("MM/DD/YYYY h:mm A")}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Status
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {announcement?.status}
                        </span>
                    </li>

                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Announcement Type
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {announcement?.announcementType}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Start Date
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {moment(announcement?.startDate).format("MM/DD/YYYY")}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-150px tw-flex">
                            <span className="tw-opacity-70">
                                Expiry Date
                            </span>
                        </span>
                        <span className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            {moment(announcement?.dateOfExpiry).format("MM/DD/YYYY")}
                        </span>
                    </li>
                    <li className="tw-flex tw-flex-col tw-space-y-5px">
                        <span className="tw-w-250px tw-flex ">
                            <span className="tw-opacity-70">
                                Announcement Description
                            </span>
                        </span>
                        <div className="tw-flex-grow tw-w-[calc(100%-9.6875rem)] tw-flex tw-justify-start tw-text-black tw-pr-20px">
                            <ReadOnlyText content={announcement?.announcementDescription} />
                        </div>
                    </li>
                </ul>

                <div className="tw-flex tw-justify-end tw-mx-4">
                    {savingAnnouncement ? <>
                                <CircularProgress style={{ color: "#12B4B9" }} ></CircularProgress>
                            </>:<>
                    {announcement?.status === "Draft" && <ButtonWarning className="tw-mx-4" onClick={confirmPublishAnnouncement}>
                        Publish
                    </ButtonWarning>}
                    {announcement?.status !== "Archived" && <ButtonPrimary className="" type="submit" onClick={() => setEditMode(true)}>
                        Edit
                    </ButtonPrimary>}
                    </>}
                </div>
            </>}

            {!editMode && announcement?.status !== "Draft" && <div className="tw-border-dashed tw-border-t-2 tw-border-gray-500 tw-pt-5 tw-mt-5">
                <div className="tw-pb-2 tw-border-b tw-border-gray-800">
                    <h6 className="tw-text-md tw-font-semibold tw-leading-6 tw-text-gray-900">Acknowlegement Records - {acknowledgements ? acknowledgements.length : 0 }</h6>
                </div>

                {acknowledgements && acknowledgements.length > 0 && <div className="tw-inline-block tw-min-w-full tw-py-2 tw-align-middle sm:tw-px-6 lg:tw-px-8">
                    <table className="tw-min-w-full tw-divide-y tw-divide-gray-300">
                    <thead>
                        <tr>
                        <th scope="col" className="tw-py-3.5 tw-pl-4 tw-pr-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 sm:tw-pl-0">
                            Employee Name
                        </th>
                        <th scope="col" className="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900">
                            Acknowledgement Date
                        </th>
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-200">
                        {acknowledgements?.map((acknowledgement) => (
                        <tr key={acknowledgement.agencyAnnouncementAcknowledgementID} >
                            <td className="tw-whitespace-nowrap tw-py-4 tw-pl-4 tw-pr-3 tw-text-sm tw-font-medium tw-text-gray-900 sm:tw-pl-0">
                            {acknowledgement.employeeName}
                            </td>
                            <td className="tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500">{moment(acknowledgement.acknowledgementDate).format("MM-DD-YY h:mm A")}</td>
                           
                        </tr>
                        ))}
                    </tbody>
                    </table>
                </div>}
            </div>}

            </>}
        </div>
    </>);
}

export default ManageAgencyAnnouncementPage;